// src/services/UserService.js
import { DEFAULT_URL } from "./ApiConfig";
/**
 * Service for handling user-related API operations
 */
const UserService = {
  getUserIfExists: async (firebaseToken) => {
    try {
      if (!firebaseToken) {
        return null; // Don't throw, just return null for non-authenticated states
      }

      const API_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const url = `${API_URL}/api/users/me`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (response.status === 404) {
        return null; // User not found, but not an error
      }

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error in getUserIfExists:", error);
      return null; // Return null on error for more graceful handling
    }
  },
  /**
   * Gets or creates a user on the server using Firebase authentication token
   *
   * @param {string} firebaseToken - The Firebase authentication token
   * @param {Object} userData - Additional user data to include
   * @returns {Promise<Object>} - User data from the server
   */
  getOrCreateUser: async (firebaseToken, userData = {}, referralCode) => {
    try {
      if (!firebaseToken) {
        throw new Error("Firebase token is required");
      }
      let uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";
      let loginMethod = sessionStorage.getItem("userLoginType");
      if (!loginMethod) {
        loginMethod = userData.phoneNumber ? "Phone" : "Email";
      }

      // Get device information
      const deviceInfo = await getUserDeviceInfo();

      // Prepare LoginMetadataDto object
      const loginMetadata = {
        deviceOS: deviceInfo.os || navigator.platform,
        deviceProduct: deviceInfo.product || "Web",
        deviceModel: deviceInfo.model || "Browser",
        deviceManufacturer: deviceInfo.manufacturer || "Web",
        deviceIdentifier: deviceInfo.identifier || getUserDeviceId(),
        deviceAppVersion: deviceInfo.appVersion || navigator.appVersion,
        deviceIp: null, // Will be set by server
        deviceCity: null, // Will be set by server
        deviceCountry: null, // Will be set by server
        deviceLatitude: deviceInfo.latitude || null,
        deviceLongitude: deviceInfo.longitude || null,
        loginMethod: loginMethod, // Web login
      };

      // Hardcoded referral code
      // const referralCode = "dimo";

      const url = `${API_URL}/api/users/get-or-create?referrer=${referralCode}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-filter": firebaseToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers":
            "Origin, X-Requested-With, Content-Type, Accept, x-user-filter",
        },
        body: JSON.stringify({
          // Send exactly the loginMetadata DTO expected by the controller
          loginMetadata: loginMetadata,
          // Include user-specific data
          ...userData,
          // Add other helpful context data
          source: "onboarding_flow",
          timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error in getOrCreateUser:", error);
      throw error;
    }
  },

  /**
   * Updates user data on the server
   *
   * @param {string} firebaseToken - The Firebase authentication token
   * @param {string} userId - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} - Updated user data
   */
  updateUser: async (firebaseToken, userId, userData) => {
    try {
      if (!firebaseToken || !userId) {
        throw new Error("Firebase token and user ID are required");
      }

      const API_URL =
        process.env.REACT_APP_API_BASE_URL || "https://api.ownli.co";

      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error in updateUser:", error);
      throw error;
    }
  },

  /**
   * Gets the user's Firebase ID token
   *
   * @param {Object} user - Firebase user object
   * @returns {Promise<string>} - Firebase ID token
   */
  getFirebaseToken: async (user) => {
    try {
      if (!user) {
        throw new Error("User is required");
      }

      // This assumes you're using Firebase v9 Auth
      return await user.getIdToken(true);
    } catch (error) {
      console.error("Error getting Firebase token:", error);
      throw error;
    }
  },
};

/**
 * Helper function to get device information
 * @returns {Object} - Device information object
 */
const getUserDeviceInfo = async () => {
  let latitude = null;
  let longitude = null;

  // Parse user agent for device info
  const userAgent = navigator.userAgent;
  let os = "Unknown";
  let manufacturer = "Unknown";
  let model = "Web Browser";

  // Detect OS
  if (/Windows/.test(userAgent)) os = "Windows";
  else if (/Macintosh|Mac OS X/.test(userAgent)) os = "MacOS";
  else if (/Linux/.test(userAgent)) os = "Linux";
  else if (/Android/.test(userAgent)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(userAgent)) os = "iOS";

  // Try to detect manufacturer and model (very simplified)
  if (/iPhone/.test(userAgent)) {
    manufacturer = "Apple";
    model = "iPhone";
  } else if (/iPad/.test(userAgent)) {
    manufacturer = "Apple";
    model = "iPad";
  } else if (/Macintosh/.test(userAgent)) {
    manufacturer = "Apple";
    model = "Mac";
  } else if (/Android/.test(userAgent)) {
    // Try to extract model from Android UA
    const androidModel = userAgent.match(/Android.*?;\s([^;)]+)/);
    if (androidModel && androidModel[1]) {
      model = androidModel[1].trim();
      // Try to extract manufacturer from model
      const parts = model.split(" ");
      if (parts.length > 0) {
        manufacturer = parts[0];
      }
    }
  }

  return {
    os,
    manufacturer,
    model,
    product: model,
    appVersion: navigator.appVersion,
    latitude,
    longitude,
    identifier: getUserDeviceId(),
  };
};

/**
 * Helper function to get or create a persistent device ID
 * @returns {string} - Device identifier
 */
const getUserDeviceId = () => {
  // Try to get existing device ID from localStorage
  let deviceId = localStorage.getItem("ownli_device_id");

  // If no device ID exists, create one and store it
  if (!deviceId) {
    deviceId =
      "web_" + Date.now() + "_" + Math.random().toString(36).substring(2, 15);
    localStorage.setItem("ownli_device_id", deviceId);
  }

  return deviceId;
};
export default UserService;
